import React from "react";
import ResizeObserver from "resize-observer-polyfill";

export enum SizeObserveMode {
    /** Base mode defines the "real" size of the widget in the target page */
    BASE,
    /** Float mode applies to parts of the widget that should be placed above other elements in the target page. */
    FLOAT
}

export interface RuntimeResizeMessageObject {
    type: string;
    src: string;
    baseSize?: boolean;
    height: number;
}

interface Props {
    children: React.ReactNode;
    mode?: SizeObserveMode;
    className?: string;
}

/**
 * The size observer renders a container to place contents in.
 * The size of the container is sent to the widget runtime on the surrounding page to scale the iframe accordingly.
 */
export const SizeObserver = ({ children, mode = SizeObserveMode.FLOAT, className }: Props) => {
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => observeElement(mode, ref.current), [ref.current, mode]);

    return (
        <div ref={ref} className={className}>
            {children}
        </div>
    );
};

const modeStore = new Map();

function calculateHeightOfElement(element: Element): number {
    return Math.ceil(element.getBoundingClientRect().height + element.getBoundingClientRect().top);
}

function sendToRuntime(baseSize: boolean, height: number) {
    window.parent.postMessage({ type: "fin:r", src: location.href, baseSize, height }, "*");
}

const observer =
    typeof window !== "undefined"
        ? new ResizeObserver(entries => {
              entries.forEach(entry => {
                  sendToRuntime(
                      modeStore.get(entry.target) === SizeObserveMode.BASE,
                      calculateHeightOfElement(entry.target)
                  );
              });
          })
        : null;

const observeElement = (mode: SizeObserveMode, ref: HTMLDivElement | null) => {
    if (!ref || !observer) {
        return;
    }

    if (modeStore.get(ref) === undefined) {
        modeStore.set(ref, mode);
        // @ts-ignore
        observer.observe(ref, { box: "border-box" });
    }

    return () => {
        modeStore.delete(ref);
        observer.unobserve(ref);
    };
};
